import axios from "axios";
import { extractError, loadRejection } from "../utils/apiErrorUtils";
import { sendAsOneAction } from "../utils/idempotencyKey";

const apiUrl = process.env.REACT_APP_API_URL || "http://localhost:5000";

/**
 * Statuses meaning "the server understood the request and refused it because
 * this trade is no longer in the state you acted on".
 *
 * Distinct from a request that never landed (no response) and from one the
 * server itself fumbled (5xx): those say nothing about the trade, so a caller
 * must not conclude its copy is wrong. A refusal in THIS set proves the local
 * copy is stale.
 */
const STALE_TRADE_STATUSES = new Set([400, 404, 409, 410]);

/**
 * The stale-transition refusals `server/trades/tradeController.js` actually
 * sends, matched on their fixed prefixes:
 *
 *   `Trade is already ${status}`                    400 — accept/decline/cancel
 *   `Only an accepted trade can be ${x} (current…)` 400 — abandon/dispute/complete
 *   `Trade request has expired`                     410 — the 48h TTL lazy-expire
 *   `Trade request not found`                       404 — deleted, or never ours
 *
 * and the three accept refusals that mean the list is out of date (B-12): a
 * card already promised to another trade (409, so the re-read brings the
 * offer's "promised" mark and drops its inline Accept), one promised a moment
 * ago, and one gone, which cancels the offer. Since owner decision Q-07 the
 * inbox no longer accepts at all — only the offer's own page does — so it no
 * longer meets these; they are kept rather than narrowed in the same change.
 *
 * Deliberately narrow. Anything unrecognised is treated as NOT stale, so the
 * fallback direction is "leave the list exactly as it was" — today's behaviour.
 */
const STALE_TRADE_MESSAGES = [
  // Owner decision Q-27: the server's plain state sentences
  // (server/trades/tradeStatusCopy.js). The old prefixes below stay for a
  // server that has not been deployed yet.
  /^this offer hasn't been accepted yet\b/i,
  /^this offer was already accepted\b/i,
  /^this offer has expired\b/i,
  /^this trade is on hold\b/i,
  /^this trade was already completed\b/i,
  /^this trade was cancelled\b/i,
  /^this trade has ended\b/i,
  /^this trade has changed\b/i,
  // Owner decision Q-24: "Meetup fell through" refused because the other
  // trader confirmed the swap meanwhile (409); the re-read shows that.
  /^the other trader has already confirmed\b/i,
  /^you have both confirmed the swap\b/i,
  /^trade is already\b/i,
  /^only an accepted trade can be\b/i,
  /^trade request has expired\b/i,
  /^trade request not found\b/i,
  /^one of these cards is already promised\b/i,
  /^another trade for one of these cards was accepted\b/i,
  /^a card in this trade is no longer available\b/i,
  // B-07: Accept refused across a block placed after the list loaded; the
  // re-read brought the trade's `blocked` mark, which dropped the inline
  // Accept the inbox had before Q-07.
  /^you can no longer trade with this collector\b/i,
];

/**
 * Did this mutation fail because the trade moved on without us? (P2-55)
 *
 * The status is authoritative whenever the caller has one — `useAxios` stamps
 * `httpStatus` onto the error it rejects with. The mutation helpers below still
 * reject with a BARE MESSAGE STRING, because `TradePresenceCheckIn`'s
 * `confirmMet` reads `typeof err === "string"` and would silently downgrade to
 * its generic "Couldn't record that" copy if that shape widened (the builder's
 * view-mode Accept, the first reader of this contract, went with Q-07); so in
 * practice the message prefixes above are what classifies a refusal today, and
 * the status is the refinement that keeps a 5xx out.
 *
 * @param {string} message  user-facing message, as `extractError` produced it
 * @param {number|null} [status]  HTTP status, when the caller has one
 */
export const isStaleTradeError = (message, status) => {
  if (status != null) return STALE_TRADE_STATUSES.has(Number(status));
  const text = String(message ?? "").trim();
  return STALE_TRADE_MESSAGES.some((re) => re.test(text));
};

export const createTradeRequest = async (tradeData) => {
  try {
    // Retrying the same offer reuses its key, so an offer that went through
    // but lost its reply comes back as sent, not as "already pending" (B-33).
    // Photos count by number only: a retry uploads them again under new names.
    // One slot for the one offer builder (the receiver is in the sig);
    // TradeRequestModal forgets it whenever the draft is reset.
    const sig = JSON.stringify(tradeData, (k, v) =>
      k === "photos" && Array.isArray(v) ? v.length : v
    );
    const { data } = await sendAsOneAction(
      "offer",
      sig,
      (headers) => axios.post(`${apiUrl}/trades`, tradeData, { headers })
    );
    return data;
  } catch (error) {
    // #147: when the server rejects specific cards it returns a structured
    // `tradeErrors` array. Carry it through alongside the fallback message so the
    // modal can turn each reason code + cardId into copy naming the actual card.
    const tradeErrors = error?.response?.data?.tradeErrors;
    const message = extractError(error, "Failed to create trade request");
    if (Array.isArray(tradeErrors) && tradeErrors.length > 0) {
      return Promise.reject({ message, tradeErrors });
    }
    return Promise.reject(message);
  }
};

// `cfg` goes to axios; `{ quiet: true }` keeps a background reload out of the
// global error snackbar (useAxios.js).
export const getMyTradeRequests = async (cfg) => {
  try {
    const { data } = await axios.get(`${apiUrl}/trades`, cfg);
    return data;
  } catch (error) {
    return Promise.reject(extractError(error, "Failed to get trade requests"));
  }
};

export const acceptTradeRequest = async (tradeId) => {
  try {
    const { data } = await axios.put(`${apiUrl}/trades/${tradeId}/accept`);
    return data;
  } catch (error) {
    return Promise.reject(extractError(error, "Failed to accept trade"));
  }
};

export const declineTradeRequest = async (tradeId) => {
  try {
    const { data } = await axios.put(`${apiUrl}/trades/${tradeId}/decline`);
    return data;
  } catch (error) {
    return Promise.reject(extractError(error, "Failed to decline trade"));
  }
};

export const cancelTradeRequest = async (tradeId) => {
  try {
    const { data } = await axios.put(`${apiUrl}/trades/${tradeId}/cancel`);
    return data;
  } catch (error) {
    return Promise.reject(extractError(error, "Failed to cancel trade"));
  }
};

// Post-acceptance exits. Either participant may take them.
export const abandonTradeRequest = async (tradeId) => {
  try {
    const { data } = await axios.put(`${apiUrl}/trades/${tradeId}/abandon`);
    return data;
  } catch (error) {
    return Promise.reject(extractError(error, "Failed to close this trade"));
  }
};

// `problem` = { reason, description } from ReportTradeProblemDialog; the server
// files it as a moderation report (P0 area D).
export const disputeTradeRequest = async (tradeId, problem = {}) => {
  try {
    const { data } = await axios.put(`${apiUrl}/trades/${tradeId}/dispute`, {
      reason: problem.reason,
      description: problem.description,
    });
    return data;
  } catch (error) {
    return Promise.reject(extractError(error, "Failed to report a problem"));
  }
};

// Confirm that the in-person swap actually happened. Both traders must call
// this before the trade is marked completed and reputation is awarded.
export const confirmTradeCompletion = async (tradeId) => {
  try {
    const { data } = await axios.put(`${apiUrl}/trades/${tradeId}/complete`);
    return data;
  } catch (error) {
    return Promise.reject(extractError(error, "Failed to confirm trade completion"));
  }
};

/** One trade, for the journey view (`/trades/:tradeId`). */
export const getTradeRequest = async (tradeId) => {
  try {
    const { data } = await axios.get(`${apiUrl}/trades/${tradeId}`);
    return data;
  } catch (error) {
    return Promise.reject(loadRejection(error, "Failed to load that trade"));
  }
};

/**
 * Suggest where and when to meet.
 *
 * A suggestion, not a decision: it does not arrange anything until the OTHER
 * trader accepts it. Sending it again replaces your own open suggestion, and is
 * also how you counter theirs — which flips who has to accept.
 *
 * Pass `safeSpotId` for a listed venue, or `venueName` for anywhere else. Both
 * need a `scheduledFor`: accepting sets `meetup.agreedAt`, and the checkpoint
 * that reads it claims the two of them know when to turn up.
 *
 * Proposing against an already-agreed meetup is a request to CHANGE it and does
 * not disturb what currently stands until the other side accepts.
 *
 * Resolves to `{ meetup }` — never the whole trade.
 */
export const proposeMeetup = async (tradeId, { safeSpotId, venueName, scheduledFor } = {}) => {
  try {
    const body = { safeSpotId, venueName, scheduledFor };
    // One suggestion per tap, even if the reply is lost and it is sent again (B-18).
    // useMeetupRetryReset forgets the slot once the other trader moves.
    const { data } = await sendAsOneAction(
      `meetup:${tradeId}`,
      JSON.stringify(body),
      (headers) => axios.post(`${apiUrl}/trades/${tradeId}/meetup/propose`, body, { headers })
    );
    return data;
  } catch (error) {
    return Promise.reject(extractError(error, "Couldn't save that suggestion"));
  }
};

/**
 * Accept a specific suggestion — the only thing that marks a meetup as
 * arranged. The server refuses this from whoever made the suggestion.
 *
 * `proposalId` is required and identifies the exact suggestion being accepted.
 * If the other side replaced it in the meantime the server answers 409 with
 * `code: "MEETUP_PROPOSAL_STALE"` rather than agreeing to a venue this user
 * never saw — so the rejection carries the code for the UI to act on, instead of
 * a sentence it would have to pattern-match.
 */
export const acceptMeetup = async (tradeId, proposalId) => {
  try {
    const { data } = await axios.post(`${apiUrl}/trades/${tradeId}/meetup/accept`, {
      proposalId,
    });
    return data;
  } catch (error) {
    const code = error?.response?.data?.code;
    const message = extractError(error, "Couldn't accept that suggestion");
    return Promise.reject(code ? { code, message } : message);
  }
};

/**
 * Report a fresh position for the meetup check-in.
 *
 * The server compares it with the other trader's, then erases both. It answers
 * with timestamps and booleans only — never a distance — so there is nothing
 * here that could be turned into a tracker. A refusal carries a `code`
 * (LOW_ACCURACY / INVALID_POSITION) the UI maps to its own copy.
 */
export const checkInAtMeetup = async (tradeId, position) => {
  try {
    const { data } = await axios.post(`${apiUrl}/trades/${tradeId}/checkin`, position);
    return data;
  } catch (error) {
    const code = error?.response?.data?.code;
    const message = extractError(error, "Could not check you in");
    return Promise.reject(code ? { code, message } : message);
  }
};

/**
 * "We're together" — confirm the meetup without sharing any location.
 *
 * Verifies once BOTH sides confirm. Weaker than the GPS or code paths: it
 * records that the two of them agree they met, not that they were in the same
 * place, and the UI says so rather than dressing it up as a location check.
 */
export const confirmTogether = async (tradeId) => {
  try {
    const { data } = await axios.post(`${apiUrl}/trades/${tradeId}/checkin/confirm`);
    return data;
  } catch (error) {
    return Promise.reject(extractError(error, "Could not record that"));
  }
};

/** The rotating code THIS user reads out for the other to type. */
export const getInPersonCode = async (tradeId) => {
  try {
    const { data } = await axios.get(`${apiUrl}/trades/${tradeId}/checkin/code`);
    return data;
  } catch (error) {
    return Promise.reject(extractError(error, "Could not get a code"));
  }
};

/** Redeem the OTHER trader's code — co-presence without sharing a location. */
export const submitInPersonCode = async (tradeId, code) => {
  try {
    const { data } = await axios.post(`${apiUrl}/trades/${tradeId}/checkin/code`, { code });
    return data;
  } catch (error) {
    const c = error?.response?.data?.code;
    const message = extractError(error, "That code didn't work");
    return Promise.reject(c ? { code: c, message } : message);
  }
};
